import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/Card";
import { useClubSettings } from "../contexts/ClubSettingsContext";
import { Button } from "./ui/Button";
import { Badge } from "./ui/Badge";
import { ConfirmationModal } from "./ui/ConfirmationModal";
import { apiService } from "../services/api";
import { formatLocalDate } from "../utils/helpers";
import { useAuth } from "../contexts/AuthContext";

export default function EvaluationCard({ evaluation, onUpdate }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { isSkillsBasedClub } = useClubSettings();

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "green";
      case "draft":
        return "yellow";
      case "completed":
        return "blue";
      default:
        return "gray";
    }
  };

  const handleTitleClick = () => {
    navigate(`/portal/club/${evaluation.club_id}/evaluation/${evaluation.id}`);
  };

  const handleStatusChange = async (newStatus) => {
    setLoading(true);
    try {
      await apiService.updateEvaluation(evaluation.id, {
        status: newStatus,
        clubId: evaluation.club_id,
      });
      onUpdate();
    } catch (error) {
      console.error("Error updating evaluation:", error);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiService.deleteEvaluation(evaluation.id);
      onUpdate();
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting evaluation:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  return (
    <Card className="evaluation-card">
      <CardHeader>
        <div className="evaluation-header">
          <CardTitle
            className="cursor-pointer hover:text-slate-600 transition-colors"
            onClick={handleTitleClick}
          >
            {evaluation.name}
          </CardTitle>
          <Badge color={getStatusColor(evaluation.status)}>
            {evaluation.status.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="evaluation-info">
          <p>
            <strong>Access Code:</strong> {evaluation.access_code}
          </p>
          <p>
            <strong>Created:</strong> {formatLocalDate(evaluation.created_at)}
          </p>
          <p>
            <strong>Participants:</strong> {evaluation.participantCount || 0}
          </p>
        </div>

        <div className="evaluation-actions">
          {evaluation.status === "draft" && (
            <Button
              size="sm"
              onClick={() => handleStatusChange("active")}
              disabled={loading}
            >
              Activate
            </Button>
          )}
          {evaluation.status === "active" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleStatusChange("completed")}
              disabled={loading}
            >
              Complete
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const resultsPath = isSkillsBasedClub
                ? `/portal/evaluation/${evaluation.id}/skill-results`
                : `/portal/evaluation/${evaluation.id}/results`;
              navigate(resultsPath);
            }}
          >
            View Results
          </Button>
          {user &&
            (user.role === "club_admin" || user.role === "super_admin") && (
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDeleteClick}
              >
                Delete
              </Button>
            )}
        </div>
      </CardContent>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Evaluation"
        message={`Are you sure you want to delete "${evaluation.name}"? This action cannot be undone and will permanently remove all associated data.`}
        confirmText="Delete Evaluation"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={deleting}
      />
    </Card>
  );
}
